import { AgGridReact } from "ag-grid-react";
import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AG_GRID_DEFAULT_COL_DEF, AG_GRID_MODULES, AG_GRID_OPTIONS, TOAST_CONTAINER_ID, TRADE_SPEND_REPORT_TAB_ID } from "../../../../shared/constants";

import useFetch from "../../../../hooks/useFetchMSAL";
import { TradeAndPromotionPlanningFetchAPI } from "../../../../models/APIResponses.model";
import { TradeSpendReportDropdownModel, TradeSpendReportSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/TradeSpendReportSlicer.model";
import { getToastOptions, usaNumberFormatter } from "../../../../shared/functions";
import { RootState } from "../../../../store";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import { tradeSpendReportTabAction } from "../../../../store/slices/trade-spend-report-tab-slice";

/**
 * Returns USD amount string.
 * @param {any} params Parameters
 * @returns {string | null} USD amount string or null
 */
const getUSDAmountString = (params: any): string | null => {
    const numStr: string | null = usaNumberFormatter(params.value, 0);
    return numStr ? `$${numStr}` : numStr;
};

const TradeSpendReportGrid = (props: { gridHeight: any }) => {
    const agGridRef = useRef<AgGridReact>(null);
    const [gridRowData, setGridRowData] = useState<any[]>([]);
    const [gridRowDataCopy, setGridRowDataCopy] = useState<any[]>([]);
    const [, fetchData] = useFetch([]);

    // Redux variables
    const dispatchFn = useDispatch();
    const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
    const selectedSlicers: TradeSpendReportSelectedSlicerModel = useSelector((state: RootState) => state.tradeSpendReportTab.selectedSlicers)
    const tabApiDataFetched: boolean = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.tabApiDataFetched)[TRADE_SPEND_REPORT_TAB_ID];
    const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
    
    const gridColDef: any = [
        {
            field: "customer_name",
            tooltipField: "customer_name",
            headerTooltip: "Customer",
            cellDataType: "text",
            cellClass: "ag-left-aligned-cell",
            headerName: "Customer",
            wrapText: true,
            autoHeight: true,
            pinned: "left",
            width: 150
        },
        {
            field: "sku_name",
            tooltipField: "sku_name",
            headerTooltip: "Product",
            cellDataType: "text",
            cellClass: "ag-left-aligned-cell",
            headerName: "Product",
            wrapText: true,
            autoHeight: true,
            pinned: "left",
            width: 150
        },
        {
            field: "promotion",
            tooltipField: "promotion",
            headerTooltip: "Promotion Name",
            cellDataType: "text",
            cellClass: "ag-left-aligned-cell",
            headerName: "Promotion Name",
            wrapText: true,
            autoHeight: true,
            pinned: "left",
            width: 150
        },
        {
            field: "Promotion Id",
            tooltipField: "Promotion Id",
            headerTooltip: "Promotion Id",
            cellDataType: "text",
            cellClass: "ag-left-aligned-cell",
            headerName: "Promotion Id",
            wrapText: true,
            autoHeight: true,
            pinned: "left",
            width: 100
        },
        {
            field: "promo_description",
            tooltipField: "promo_description",
            headerTooltip: "Promotion Description",
            cellDataType: "text",
            cellClass: "ag-left-aligned-cell",
            headerName: "Promotion Description",
            wrapText: true,
            autoHeight: true,
            width: 150
        },
        {
            field: "promotion_type",
            tooltipField: "promotion_type",
            headerTooltip: "Promotion Type",
            cellDataType: "text",
            cellClass: "ag-left-aligned-cell",
            headerName: "Promotion Type",
            width: 150
        },
        {
            field: "Promotion Tactic",
            tooltipField: "Promotion Tactic",
            headerTooltip: "Promotion Tactic",
            cellDataType: "text",
            cellClass: "ag-left-aligned-cell",
            headerName: "Promotion Tactic",
            width: 150
        },
        {
            colId: "trade_spend",
            field: "trade_spend",
            minWidth: 100,
            headerTooltip: "Trade Spend ($)",
            cellDataType: "text",
            cellClass: "ag-right-aligned-cell",
            headerName: "Trade Spend ($)",
            valueFormatter: getUSDAmountString,
            tooltipValueGetter: (params: any) => params.valueFormatted
        },
        {
            colId: "total_shipment",
            field: "total_shipment",
            minWidth: 100,
            tooltipField: "total_shipment",
            headerTooltip: "Total Shipment",
            cellDataType: "text",
            cellClass: "ag-right-aligned-cell",
            headerName: "Total Shipment",
            valueFormatter: (params: any): any => usaNumberFormatter(params.value, 0),
            tooltipValueGetter: (params: any) => params.valueFormatted
        },
        {
            colId: "gross_sales",
            field: "gross_sales",
            minWidth: 100,
            tooltipField: "gross_sales",
            headerTooltip: "Gross Sales ($)",
            cellDataType: "text",
            cellClass: "ag-right-aligned-cell",
            headerName: "Gross Sales ($)",
            valueFormatter: getUSDAmountString,
            tooltipValueGetter: (params: any) => params.valueFormatted
        },
        {
            colId: "trade_spend_ratio",
            field: "trade_spend_ratio",
            minWidth: 100,
            headerTooltip: "T:S Ratio (%)",
            cellDataType: "text",
            cellClass: "ag-right-aligned-cell",
            headerName: "T:S Ratio (%)",
            valueFormatter: (params: any): any => `${usaNumberFormatter(params.value * 100, 2)}%`,
            tooltipValueGetter: (params: any) => `${usaNumberFormatter(params.value * 100, 2)}%`
        },
        {
            colId: "profit",
            field: "profit",
            minWidth: 100,
            tooltipField: "profit",
            headerTooltip: "Profit ($)",
            cellDataType: "text",
            cellClass: "ag-right-aligned-cell",
            headerName: "Profit ($)",
            valueFormatter: getUSDAmountString,
            tooltipValueGetter: (params: any) => params.valueFormatted
        },
        {
            colId: "profit_percent",
            field: "profit_percent",
            minWidth: 100,
            headerTooltip: "Profit Percent (%)",
            cellDataType: "text",
            cellClass: "ag-right-aligned-cell",
            headerName: "Profit Percent (%)",
            valueFormatter: (params: any): any => `${usaNumberFormatter(params.value * 100, 2)}%`,
            tooltipValueGetter: (params: any) => `${usaNumberFormatter(params.value * 100, 2)}%`
        },
        {
            colId: "cost_of_goods_sold",
            field: "cost_of_goods_sold",
            minWidth: 100,
            tooltipField: "cost_of_goods_sold",
            headerTooltip: "Cost of Goods Sold ($)",
            cellDataType: "text",
            cellClass: "ag-right-aligned-cell",
            headerName: "Cost of Goods Sold ($)",
            valueFormatter: getUSDAmountString,
            tooltipValueGetter: (params: any) => params.valueFormatted
        },
    ];

    /**
     * Async function to fetch data from POST /get-trade-spend-report API
     */
    const fetchTradeSpendReportData = useCallback(async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching Trade Spend Report data...', {

            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: any[];
                slicers: TradeSpendReportDropdownModel[];
            }>> =
                await fetchData("/trade-spend-report", {
                    method: "POST",
                    data: {
                        customer_id: globalCustomer.id,
                        skus: selectedSlicers.skus,
                        brands: selectedSlicers.brands,
                        promotions: selectedSlicers.promotions,
                        promotionType: selectedSlicers.promotionType
                    }
                });

            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            if (fetchAPIResponse.data.data) {
                const apiGridData: any[] = fetchAPIResponse.data.data.result;

                setGridRowData(structuredClone(apiGridData));
                setGridRowDataCopy(structuredClone(apiGridData));

                dispatchFn(tradeSpendReportTabAction.setSlicers(structuredClone(fetchAPIResponse.data.data.slicers)));
                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [TRADE_SPEND_REPORT_TAB_ID]: true
                }));

            } else {
                toast.error('Error in fetching Trade Spend Report data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Trade Spend Report data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    }, [globalCustomer.id, selectedSlicers, dispatchFn, fetchData]);

    useEffect(() => {
        if (
            !tabApiDataFetched && 
            activeSubTabId === TRADE_SPEND_REPORT_TAB_ID
        ) {
            fetchTradeSpendReportData();
        }
    }, [selectedSlicers, tabApiDataFetched, activeSubTabId, fetchTradeSpendReportData]);


    return (
        <>
            <div className="ag-theme-balham mt-2" style={{
                height: props.gridHeight, maxHeight: props.gridHeight,
                width: '100%', overflowX: 'auto', overflowY: 'auto'
            }}>
                <AgGridReact
                    ref={agGridRef}
                    rowData={gridRowDataCopy}
                    columnDefs={gridColDef}
                    gridOptions={{
                        ...(AG_GRID_OPTIONS as any),
                        rowHeight: 22,
                        suppressMenuHide: true,
                        suppressColumnVirtualisation: true,
                        autoSizeStrategy: {
                            type: "fitCellContents",
                            skipHeader: true,
                            colIds: [
                                "trade_spend",
                                "total_shipment",
                                "gross_sales",
                                "trade_spend_ratio",
                                "profit",
                                "profit_percent",
                                "cost_of_goods_sold"
                            ]
                        }
                    }}
                    defaultColDef={{
                        ...AG_GRID_DEFAULT_COL_DEF
                    }}
                    modules={[
                        ...AG_GRID_MODULES
                    ]}
                />
            </div>
        </>
    )
}
export default TradeSpendReportGrid